import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "../i18n";
import { useShopEvent } from "../hooks/useShopEvent";
import { formatMoney } from "../pos/utils/money";
import { playChime, setSoundEnabled, soundEnabled } from "../utils/chime";
import { formatTime, relativeDay } from "../utils/dates";

/**
 * "A customer just reserved something", on whichever screen is open.
 *
 * Arrives over the shop's live connection the moment the order is placed,
 * with a chime, because a cashier mid-sale is not watching a list. It stays
 * until someone acknowledges it: an order that needs putting aside should not
 * disappear on a timer while nobody was looking.
 *
 * Portalled above everything, including the till's PIN lock, so a locked
 * register still announces the order. `canView` is off while locked, since
 * the orders cannot be opened until someone unlocks.
 */
const MAX_SHOWN = 3;

export default function NewOrderAlert({
  currency = "TND",
  onView,
  canView = true,
}) {
  const { t } = useI18n();
  const [orders, setOrders] = useState([]);
  const [sound, setSound] = useState(soundEnabled);

  useShopEvent("preorder:created", (order) => {
    setOrders((current) =>
      [order, ...current.filter((item) => item.id !== order.id)].slice(
        0,
        MAX_SHOWN,
      ),
    );
    playChime();
  });

  const dismiss = (id) =>
    setOrders((current) => current.filter((item) => item.id !== id));

  const toggleSound = () => {
    const next = !sound;
    setSoundEnabled(next);
    setSound(next);
    if (next) playChime();
  };

  if (orders.length === 0) return null;

  return createPortal(
    <div
      className="fixed top-3 inset-x-3 z-[80] flex flex-col items-center gap-2 pointer-events-none"
      aria-live="assertive"
    >
      {orders.map((order) => (
        <div
          key={order.id}
          role="alert"
          className="pointer-events-auto w-full max-w-md rounded-2xl bg-amber-400 text-slate-900 shadow-2xl ring-1 ring-amber-600/30 p-4"
        >
          <div className="flex items-start gap-3">
            <span className="text-2xl leading-none" aria-hidden="true">
              🛎️
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-extrabold">{t("alerts.newOrder.title")}</p>
              <p className="text-sm font-semibold truncate">
                {order.user_name || t("alerts.newOrder.customer")} ·{" "}
                {t("common.items", { count: order.units || 0 })} ·{" "}
                {formatMoney(order.total_amount, currency)}
              </p>
              <p className="text-sm">
                {t("alerts.newOrder.pickup", {
                  day: relativeDay(order.pickup_date),
                  time: formatTime(order.pickup_time),
                })}
              </p>
            </div>
            <button
              type="button"
              onClick={toggleSound}
              title={t(
                sound ? "alerts.newOrder.soundOn" : "alerts.newOrder.soundOff",
              )}
              aria-label={t(
                sound ? "alerts.newOrder.soundOn" : "alerts.newOrder.soundOff",
              )}
              className="shrink-0 h-9 w-9 rounded-full bg-white/50 hover:bg-white/70 text-lg"
            >
              {sound ? "🔔" : "🔕"}
            </button>
          </div>

          <div className="mt-3 flex gap-2">
            {canView && onView && (
              <button
                type="button"
                onClick={() => {
                  dismiss(order.id);
                  onView(order);
                }}
                className="flex-1 h-10 rounded-xl bg-slate-900 text-white font-bold"
              >
                {t("alerts.newOrder.view")}
              </button>
            )}
            <button
              type="button"
              onClick={() => dismiss(order.id)}
              className="flex-1 h-10 rounded-xl bg-white/60 hover:bg-white/80 font-bold"
            >
              {t("alerts.newOrder.dismiss")}
            </button>
          </div>
        </div>
      ))}
    </div>,
    document.body,
  );
}
